import React, { useEffect, memo } from 'react';
import { ArrowLeft } from 'lucide-react';
import { SEO } from '../components/SEO';
// Project data in the order: VISA, Williams F1, Mobile app, Secure messenger, Telecom brand, Olympic committee
const allProjects = [{
  id: 'sochi-olympics-visa',
  title: 'Product-Led Growth: Sochi Olympics VISA Subway Campaign',
  sections: [{
    heading: 'Goal',
    content: "Drive brand awareness and positive association for VISA during the 2014 Sochi Olympics through an innovative, health-focused activation in Moscow's subway system."
  }, {
    heading: 'Challenge',
    content: "Create memorable brand engagement that would generate organic media coverage and connect VISA's payment innovation with the Olympic spirit of athleticism and achievement."
  }, {
    heading: 'Solution',
    content: 'A gamified campaign was developed featuring motion-sensor enabled ticket machines that dispensed free subway rides to passengers who completed 30 squats. The activation combined fitness, technology, and practical reward to create a unique brand experience.'
  }, {
    heading: 'Results',
    content: 'Generated over $500,000 in earned media value with coverage across major outlets including Wall Street Journal, CNN, Forbes, BBC, and USA Today. The campaign became a viral sensation, demonstrating how creative product integration can amplify brand reach beyond traditional advertising.'
  }, {
    heading: 'Key Insights',
    content: "Sometimes the most effective marketing doesn't feel like marketing - by solving a real problem (ticket cost) through an engaging experience (gamified fitness), the campaign created genuine value that audiences wanted to share."
  }]
}, {
  id: 'williams-f1',
  title: 'Go-To-Market Strategy: Williams F1 Market Expansion',
  sections: [{
    heading: 'Goal',
    content: "Enable Williams F1's successful entry into a new international market by developing a comprehensive go-to-market strategy that identified key partnerships and provided cultural business intelligence."
  }, {
    heading: 'Challenge',
    content: 'Navigate the complexities of international market expansion for a premium motorsport brand, including identifying the right strategic partners, understanding local business customs, and leveraging existing networks to accelerate market entry in a relationship-driven industry.'
  }, {
    heading: 'Solution',
    content: 'Developed a comprehensive market entry strategy document featuring:\n\n- Curated list of high-level, warm contacts including potential partners and sponsors with direct access to decision-makers\n\n- Detailed analysis of local business culture, customs, and relationship-building protocols specific to the target market\n\n- Strategic recommendations for partnership approach and sponsor engagement tailored to regional preferences'
  }, {
    heading: 'Results',
    content: 'Williams F1 successfully entered the target market using the strategic framework and contact network provided:\n\n- Established foundation for local partnerships through warm introductions and cultural business intelligence\n\n- Reduced market entry timeline by leveraging pre-qualified, high-level contacts rather than cold outreach'
  }, {
    heading: 'Key Insights',
    content: 'In premium B2B markets like Formula 1, success depends heavily on relationship capital and cultural understanding. A well-researched go-to-market strategy that combines warm network access with local business intelligence can significantly accelerate market entry and partnership development.'
  }]
}, {
  id: 'mobile-app',
  title: 'Product Strategy & UX Design: Mobile App',
  sections: [{
    heading: 'Goal',
    content: 'Launch a new mobile app from concept to market, driving user acquisition and engagement in the competitive privacy/security app space while establishing a scalable technical foundation.'
  }, {
    heading: 'Challenge',
    content: 'Enter a saturated market dominated by established players, while balancing user experience simplicity with technical complexity. Needed to define product vision, create intuitive user flows, and ensure robust technical architecture for a security-focused application.'
  }, {
    heading: 'Solution',
    content: 'Owned end-to-end product development from vision to execution:\n\n- Conducted comprehensive market and competitor analysis to identify user experience gaps\n\n- Designed complete user flow and UX architecture prioritizing simplicity and trust-building\n\n- Defined MVP scope based on user research insights, focusing on core functionality with streamlined onboarding\n\n- Collaborated closely with engineering leadership to establish technical requirements, API specifications, and scalability benchmarks\n\n- Led cross-functional execution across design and engineering teams'
  }, {
    heading: 'Results',
    content: '- User Growth: 30% increase in customer base and 30% growth in downloads\n\n- Engagement: 25% improvement in user engagement metrics\n\n- Revenue: 15% year-over-year growth in subscription revenue\n\n- Technical Performance: 25% reduction in production incidents through proactive technical debt management'
  }, {
    heading: 'Key Insights',
    content: 'In security-focused products, user trust is built through both seamless experience design and reliable technical performance. Balancing user research-driven simplicity with robust engineering foundations was critical to achieving sustainable growth in a competitive market.'
  }]
}, {
  id: 'secure-messenger',
  title: 'Secure Messenger: Product Growth and Social Media Strategy',
  sections: [{
    heading: 'Goal',
    content: 'Drive user acquisition and platform engagement for a secure messaging app through a product growth strategy, including creative content that differentiates the brand in the crowded privacy-focused communication market.'
  }, {
    heading: 'Challenge',
    content: 'Secure messaging apps face multiple challenges:\n\n- Communicating complex privacy benefits in an accessible way.\n\n- The target audience needed to understand the value of secure communication without technical jargon.\n\n- Required a creative approach to stand out among established competitors like Signal and WhatsApp.'
  }, {
    heading: 'Solution',
    content: "Developed an overall product growth strategy including creative content that reimagined classical literature to address modern digital privacy concerns:\n\n- Adapted Homer's The Odyssey to contemporary times, featuring Odysseus and Penelope communicating through a secure messenger.\n\n- Created narrative content that naturally demonstrated product benefits through storytelling rather than direct promotion.\n\n- Leveraged the timeless appeal of classical literature to create memorable, shareable content that educated users about the importance of privacy."
  }, {
    heading: 'Results',
    content: 'The campaign achieved significant growth across key metrics:\n\n- Traffic Growth: Achieved a 5x increase in platform visits.\n\n- Brand Differentiation: Created unique positioning in the secure messaging market through literary storytelling.\n\n- User Education: Successfully communicated complex privacy concepts through accessible narrative content.'
  }, {
    heading: 'Key Insights',
    content: 'Complex technical products can achieve breakthrough marketing results by connecting functional benefits to universal human stories. Creative storytelling that entertains while educating can drive significantly higher engagement than traditional feature-focused messaging.'
  }]
}, {
  id: 'telecom-brand',
  title: 'Brand Positioning Strategy: Global Telecom Company',
  sections: [{
    heading: 'Goal',
    content: 'Redefine market positioning for a premium global telecommunications company to better align with high-value client needs and drive engagement.'
  }, {
    heading: 'Challenge',
    content: "The company faced several positioning challenges:\n\n- Existing positioning failed to communicate premium value proposition effectively\n\n- Complex service portfolio wasn't clearly differentiated for high-end market segments\n\n- Premium clients required clear messaging that addressed their specific business needs"
  }, {
    heading: 'Solution',
    content: 'Developed targeted positioning strategy for premium market:\n\n- Redefined core value propositions connecting telecom services to executive business requirements\n\n- Segmented high-value client categories and created tailored benefit frameworks for each premium segment\n\n- Aligned positioning with client demands including secure communications, global coverage, and reliable service'
  }, {
    heading: 'Results',
    content: 'The repositioning delivered measurable impact:\n\n- Qualified Traffic Growth: Achieved 8x increase in website traffic with higher-value prospects\n\n- Premium Market Clarity: Established clear differentiation in luxury telecom segment\n\n- Client Engagement: Improved connection with C-suite and enterprise decision-makers'
  }, {
    heading: 'Key Insights',
    content: 'Premium positioning requires focusing on specific client needs rather than broad market appeal. Clear value communication for high-end segments can dramatically improve qualified lead generation.'
  }]
}, {
  id: 'ioc',
  title: 'Stakeholder Ecosystem Analysis: Research for the International Olympic Committee',
  sections: [{
    heading: 'Goal',
    content: "Enhance the International Olympic Committee's strategic planning capabilities through comprehensive stakeholder analysis across its global ecosystem."
  }, {
    heading: 'Challenge',
    content: 'The IOC faced several strategic challenges:\n\n- Complex multi-stakeholder environment required systematic analysis to identify improvement opportunities and competitive advantages\n\n- IOC needed enhanced strategic planning framework to better coordinate with diverse external partners while staying competitive against emerging sports organizations and entertainment alternatives\n\n- Senior leadership required actionable roadmap for improving partnership effectiveness and maintaining market position'
  }, {
    heading: 'Solution',
    content: 'Contributed to comprehensive six-month strategic initiative as part of a 4-person team:\n\n- Conducted deep stakeholder analysis across 10+ key external partners to map relationships and identify synergies\n\n- Developed strategic framework for planning enhancement that addresses competitive pressures\n\n- Created actionable roadmap with clear implementation phases and success metrics\n\n- Presented findings and recommendations directly to senior IOC leadership team'
  }, {
    heading: 'Results',
    content: "The initiative delivered significant strategic value:\n\n- Strategic Enhancement: Successfully refined IOC's strategic planning capabilities through systematic partner analysis\n\n- Leadership Direction: Delivered executive-level roadmap that provided clear guidance for competitive positioning\n\n- Partnership Effectiveness: Established framework for more effective coordination across complex stakeholder network"
  }, {
    heading: 'Key Insights',
    content: 'Complex global organizations benefit significantly from systematic stakeholder analysis that transforms relationships and maintains competitive advantage in evolving markets.'
  }]
}];
export function SelectedWorkPage() {
  useEffect(() => {
    // Scroll to project section if hash is present in URL
    if (window.location.hash) {
      const id = window.location.hash.substring(1);
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }, 100);
      }
    }
  }, []);
  return <>
      <SEO title="Selected Work | Katia Engalycheva - Product & Marketing Strategy Portfolio" description="Explore case studies showcasing product strategy, marketing campaigns, and UX design work including VISA Olympics campaign, Williams F1 expansion, mobile app development, and IOC strategic planning." url="https://theideasboutique.com/selected-work" keywords="product strategy portfolio, marketing case studies, UX design projects, go-to-market strategy, brand positioning, VISA campaign, Williams F1, mobile app development, IOC consulting" type="profile" data-id="element-237" />
      <div className="min-h-screen w-full bg-[#E8693B] text-white font-sans selection:bg-white selection:text-[#E8693B]" data-id="element-238">
        {/* Header Navigation */}
        <header className="w-full bg-white/5 backdrop-blur-sm sticky top-0 z-50 border-b border-white/10" data-id="element-239">
          <div className="w-full max-w-5xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between" data-id="element-240">
            <div className="flex items-center gap-3" data-id="element-241">
              <span className="text-lg md:text-xl font-semibold" data-id="element-242">
                Katia Engalycheva
              </span>
              <span className="text-white/50" data-id="element-243">—</span>
              <span className="text-sm md:text-base text-white font-light" data-id="element-244">
                Marketing & Product Strategist
              </span>
            </div>

            <nav className="flex items-center gap-8" data-id="element-245">
              <a href="/#about" className="text-sm md:text-base text-white hover:opacity-70 transition-opacity cursor-pointer" data-id="element-246">
                About
              </a>
              <a href="/#work" className="text-sm md:text-base text-white hover:opacity-70 transition-opacity cursor-pointer" data-id="element-247">
                Selected Work
              </a>
              <a href="/#speaking" className="text-sm md:text-base text-white hover:opacity-70 transition-opacity cursor-pointer" data-id="element-248">
                Speaking
              </a>
              <a href="/#contact" className="text-sm md:text-base text-white hover:opacity-70 transition-opacity cursor-pointer" data-id="element-249">
                Contact
              </a>
            </nav>
          </div>
        </header>

        <main className="w-full max-w-4xl mx-auto px-6 md:px-12 pt-12 pb-20" data-id="element-250">
          {/* Back Button */}
          <a href="/#work" className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-16 group" data-id="element-251">
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" data-id="element-252" />
            <span data-id="element-253">Back to Portfolio</span>
          </a>

          {/* All Projects */}
          {allProjects.map((project, projectIndex) => <div key={project.id} id={project.id} className="mb-32 scroll-mt-24" data-id="element-254">
              {/* Project Title */}
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-3" data-id="element-255">
                {project.title}
              </h2>

              <div className="h-px bg-white/20 my-16" data-id="element-256"></div>

              {/* Project Sections */}
              <div className="space-y-16" data-id="element-257">
                {project.sections.map((section, index) => <div key={index} className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 md:gap-12 items-start" data-id="element-258">
                    <div className="border-l-4 border-white pl-6" data-id="element-259">
                      <h3 className="text-xl md:text-2xl font-bold" data-id="element-260">
                        {section.heading}
                      </h3>
                    </div>
                    <div data-id="element-261">
                      <p className="text-lg md:text-xl leading-relaxed font-light text-white/90 whitespace-pre-line" data-id="element-262">
                        {section.content}
                      </p>
                    </div>
                  </div>)}
              </div>

              {/* Divider between projects (except after last project) */}
              {projectIndex < allProjects.length - 1 && <div className="h-px bg-white/20 mt-32" data-id="element-263"></div>}
            </div>)}

          {/* Back to Portfolio Button */}
          <div className="flex justify-center mt-20" data-id="element-264">
            <a href="/#work" className="inline-block px-12 py-4 bg-white text-[#E8693B] text-lg font-semibold rounded-lg hover:bg-white/90 transition-all shadow-lg hover:shadow-xl" data-id="element-265">
              Back to Portfolio
            </a>
          </div>
        </main>
      </div>
    </>;
}